import * as i0 from '@angular/core';
import { InjectionToken, NgModule, Optional, SkipSelf } from '@angular/core';
import { createServiceApi, serviceTypes, combineEffectsWithProviders, API_PREFIX } from '@essent/common';
export { Gender, LegalEntities, PreferredChannel, SegmentType } from '@essent/common';
import * as i1 from '@ngrx/effects';
import { EffectsModule } from '@ngrx/effects';

const apiVersion$9 = '1';
const getCustomerConsentApi = createServiceApi('[CustomerState] Get customer consent', 'get', serviceTypes(), {
    getUrl: `/customer/consents/v${apiVersion$9}`,
    version: apiVersion$9,
    getParams: payload => payload
});

const apiVersion$8 = '1';
const patchCustomerConsentApi = createServiceApi('[CustomerState] Patch customer consent', 'patch', serviceTypes(), {
    getUrl: `/customer/consents/v${apiVersion$8}`,
    version: apiVersion$8,
    getParams: ({ params }) => params,
    getBody: ({ body }) => body
});

const apiVersion$7 = '1';
const postCustomerConsentApi = createServiceApi('[CustomerState] Post customer consent', 'post', serviceTypes(), {
    getUrl: `/customer/consents/v${apiVersion$7}`,
    version: apiVersion$7,
    getParams: ({ params }) => params,
    getBody: ({ body }) => body
});

const apiVersion$6 = '3';
const getCorrespondenceDetailsApi = createServiceApi('[CustomerState] Get correspondence details', 'get', serviceTypes(), {
    getUrl: `/customer/customers/correspondencedetails/v${apiVersion$6}`,
    version: apiVersion$6,
    getParams: payload => payload
});

const apiVersion$5 = '1';
const putCorrespondenceDetailsApi = createServiceApi('[CustomerState] Put correspondence details', 'put', serviceTypes(), {
    getUrl: `/customer/customers/correspondencedetails/v${apiVersion$5}`,
    version: apiVersion$5,
    getParams: ({ accountId }) => ({ accountId }),
    getBody: ({ accountId, ...data }) => data
});

const apiVersion$4 = '1';
const createOnlineAccountApi = createServiceApi('[Customer] Create Online Account', 'post', serviceTypes(), {
    getUrl: `/customer/onlineaccount/v${apiVersion$4}`,
    version: apiVersion$4,
    getHeaders: input => {
        const headers = { Accept: 'application/json' };
        if (input.recaptchaToken) {
            headers['recaptcha-token'] = input.recaptchaToken;
        }
        return headers;
    },
    getBody: ({ body }) => body
});

const apiVersion$3 = '1';
const getCustomerApi = createServiceApi('[CustomerState] Get customer', 'get', serviceTypes(), {
    getUrl: accountId => `/customer/customers/${accountId}/v${apiVersion$3}`,
    version: apiVersion$3,
    defaultHeaders: {
        Accept: 'application/json'
    }
});

const apiVersion$2 = '1';
const putCustomerApi = createServiceApi('[CustomerState] Put customer', 'put', serviceTypes(), {
    getUrl: ({ accountId }) => `/customer/customers/${accountId}/v${apiVersion$2}`,
    version: apiVersion$2,
    getBody: ({ accountId, ...data }) => data
});

const apiVersion$1 = '1';
const searchCustomerApi = createServiceApi('[CustomerState] Search customer', 'get', serviceTypes(), {
    getUrl: `/customer/customers/v${apiVersion$1}`,
    version: apiVersion$1,
    getParams: payload => payload
});

const apiVersion = '2';
const getNameChangeReasonsApi = createServiceApi('[GetNameChangeReasonsState] Get name change reasons', 'get', serviceTypes(), {
    getUrl: `/customer/customers/namechangereasons/v${apiVersion}`,
    version: apiVersion,
    getParams: payload => payload
});

/**
 * @ignore
 */
const API = new InjectionToken('api-customer', {
    factory: () => ''
});

const { EFFECTS, PROVIDERS } = combineEffectsWithProviders(getCorrespondenceDetailsApi, searchCustomerApi, getCustomerApi, putCustomerApi, putCorrespondenceDetailsApi, getCustomerConsentApi, patchCustomerConsentApi, postCustomerConsentApi, getNameChangeReasonsApi, createOnlineAccountApi);
/**
 * Module containing all providers.
 *
 * ### Example usage
 *
 * #### Normal
 * ```typescript
 * providers: [
 *     CustomerCoreModule,
 *     ...
 * ]
 * ```
 *
 * #### Custom API
 *
 * ```typescript
 * providers: [
 *     CustomerCoreModule.forRoot({
 *         api: 'https://my-api.com'
 *     }),
 *     ...
 * ]
 * ```
 */
class CustomerCoreModule {
    constructor(parentModule) {
        if (parentModule) {
            throw new Error('CustomerCoreModule is imported more than once.');
        }
    }
    static forRoot(config) {
        return {
            ngModule: CustomerCoreModule,
            providers: [{ provide: API, useValue: config ? config.api : undefined }]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: CustomerCoreModule, deps: [{ token: CustomerCoreModule, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: CustomerCoreModule, imports: [i1.EffectsFeatureModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: CustomerCoreModule, providers: [PROVIDERS, { provide: API_PREFIX, useExisting: API }], imports: [EffectsModule.forFeature(EFFECTS)] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: CustomerCoreModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [EffectsModule.forFeature(EFFECTS)],
                    providers: [PROVIDERS, { provide: API_PREFIX, useExisting: API }]
                }]
        }], ctorParameters: () => [{ type: CustomerCoreModule, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }] });

const { getCustomerConsent, getCustomerConsentSuccess, getCustomerConsentError, getCustomerConsentClear, getCustomerConsentActions } = getCustomerConsentApi;
const { postCustomerConsent, postCustomerConsentSuccess, postCustomerConsentError, postCustomerConsentClear, postCustomerConsentActions } = postCustomerConsentApi;
const { patchCustomerConsent, patchCustomerConsentSuccess, patchCustomerConsentError, patchCustomerConsentClear, patchCustomerConsentActions } = patchCustomerConsentApi;

var ConsentTypeId;
(function (ConsentTypeId) {
    ConsentTypeId["VM_REMINDER_MAIL"] = "VMR";
    ConsentTypeId["VTK_CONSENT"] = "VTK";
    ConsentTypeId["RVV_POST"] = "RVP";
    ConsentTypeId["CAMPAIGNS_SOCIAL_MEDIA"] = "CSM";
    ConsentTypeId["PROFILING_CONSENT"] = "PRF";
})(ConsentTypeId || (ConsentTypeId = {}));

const { getCorrespondenceDetails, getCorrespondenceDetailsSuccess, getCorrespondenceDetailsError, getCorrespondenceDetailsClear, getCorrespondenceDetailsActions } = getCorrespondenceDetailsApi;
const { putCorrespondenceDetails, putCorrespondenceDetailsSuccess, putCorrespondenceDetailsError, putCorrespondenceDetailsClear, putCorrespondenceDetailsActions } = putCorrespondenceDetailsApi;

var DigitalInvoicePreferences;
(function (DigitalInvoicePreferences) {
    DigitalInvoicePreferences["YES"] = "yes";
    DigitalInvoicePreferences["NO"] = "no";
    DigitalInvoicePreferences["UNKNOWN"] = "unknown";
})(DigitalInvoicePreferences || (DigitalInvoicePreferences = {}));

const { getCustomer, getCustomerSuccess, getCustomerError, getCustomerClear, getCustomerActions } = getCustomerApi;
const { putCustomer, putCustomerSuccess, putCustomerError, putCustomerClear, putCustomerActions } = putCustomerApi;
const { searchCustomer, searchCustomerSuccess, searchCustomerError, searchCustomerClear, searchCustomerActions } = searchCustomerApi;

/**
 * Enum required by [SearchAccountPayload]{@link ../interfaces/SearchAccountPayload.html} if searching for a postcode and house number.
 */
var AddressTypes;
(function (AddressTypes) {
    AddressTypes["SUPPLY"] = "delivery";
    AddressTypes["CORRESPONDENCE"] = "correspondence";
})(AddressTypes || (AddressTypes = {}));

const { getNameChangeReasons, getNameChangeReasonsSuccess, getNameChangeReasonsError, getNameChangeReasonsClear, getNameChangeReasonsActions } = getNameChangeReasonsApi;

var NameChangeReason;
(function (NameChangeReason) {
    NameChangeReason["CHANGE_NAME_CONTRACTING_PARTY"] = "change_name_contracting_party";
    NameChangeReason["TRANSFER_CONTRACT_DECEASED"] = "transfer_contract_deceased";
    NameChangeReason["CHANGE_COMPANY_NAME_TYPO"] = "change_company_name_typo";
    NameChangeReason["CORRESPONDENCE_TO_RELATIVES_DECEASED"] = "correspondence_to_relatives_deceased";
    NameChangeReason["CHANGE_LEGAL_FORM_NOT_CRITICAL"] = "change_legal_form_not_critical";
})(NameChangeReason || (NameChangeReason = {}));

// NB: `heer / mevrouw` includes the 2 spaces. This is what the API returns.
var TitleValues;
(function (TitleValues) {
    TitleValues["MEVROUW"] = "mevrouw";
    TitleValues["NABESTAANDE_VAN"] = "nabestaande van";
    TitleValues["HEER"] = "heer";
    TitleValues["HEER_MEVROUW"] = "heer / mevrouw";
})(TitleValues || (TitleValues = {}));

const { createOnlineAccountActions, createOnlineAccount, createOnlineAccountSuccess, createOnlineAccountError, createOnlineAccountClear } = createOnlineAccountApi;

/*
 * Public API Surface of customer
 */

/**
 * Generated bundle index. Do not edit.
 */

export { API, AddressTypes, ConsentTypeId, CustomerCoreModule, DigitalInvoicePreferences, NameChangeReason, TitleValues, createOnlineAccount, createOnlineAccountActions, createOnlineAccountClear, createOnlineAccountError, createOnlineAccountSuccess, getCorrespondenceDetails, getCorrespondenceDetailsActions, getCorrespondenceDetailsClear, getCorrespondenceDetailsError, getCorrespondenceDetailsSuccess, getCustomer, getCustomerActions, getCustomerClear, getCustomerConsent, getCustomerConsentActions, getCustomerConsentClear, getCustomerConsentError, getCustomerConsentSuccess, getCustomerError, getCustomerSuccess, getNameChangeReasons, getNameChangeReasonsActions, getNameChangeReasonsClear, getNameChangeReasonsError, getNameChangeReasonsSuccess, patchCustomerConsent, patchCustomerConsentActions, patchCustomerConsentClear, patchCustomerConsentError, patchCustomerConsentSuccess, postCustomerConsent, postCustomerConsentActions, postCustomerConsentClear, postCustomerConsentError, postCustomerConsentSuccess, putCorrespondenceDetails, putCorrespondenceDetailsActions, putCorrespondenceDetailsClear, putCorrespondenceDetailsError, putCorrespondenceDetailsSuccess, putCustomer, putCustomerActions, putCustomerClear, putCustomerError, putCustomerSuccess, searchCustomer, searchCustomerActions, searchCustomerClear, searchCustomerError, searchCustomerSuccess };
